import { z } from 'zod';
import { formStepContentSchema, type FormStepContentInput } from './form-step-content';
import { FormContentType, MAX_NESTED_QUESTION_DEPTH } from '../../constants';

const nestedQuestionsDepth = (content: FormStepContentInput): number => {
  const children = content.nested_questions ?? [];
  if (children.length === 0) return 0;
  return 1 + Math.max(...children.map(nestedQuestionsDepth));
};

export const formStepSchema = z.object({
  id: z.number().int().positive().optional(),
  step: z.number().int(),
  form_id: z.number().int().positive().optional(),
  form_steps_content: z
    .array(formStepContentSchema)
    .min(1, { message: 'Each step must have at least one content block' })
    .superRefine((contents, ctx) => {
      // A step holds at most one content block of each type, so all questions must live in one question block.
      const seenTypes = new Set<string>();

      contents.forEach((content, index) => {
        if (nestedQuestionsDepth(content) > MAX_NESTED_QUESTION_DEPTH) {
          ctx.addIssue({
            code: 'custom',
            path: [index, 'nested_questions'],
            message: `Nested questions cannot exceed ${MAX_NESTED_QUESTION_DEPTH} levels`,
          });
        }

        const key = content.type;
        if (seenTypes.has(key)) {
          ctx.addIssue({
            code: 'custom',
            path: [index, 'type'],
            message: 'Each step can contain at most one content block of each type',
          });
        }
        seenTypes.add(key);
      });

      // The contact block, if present, must be the last content block in the step.
      const contactIndex = contents.findIndex(
        (content) => FormContentType.CONTACT === content.type
      );
      if (contactIndex !== -1 && contactIndex !== contents.length - 1) {
        ctx.addIssue({
          code: 'custom',
          path: [contactIndex, 'type'],
          message: 'The contact must be the last content block in the step',
        });
      }
    }),
});
export type FormStepInput = z.infer<typeof formStepSchema>;
